import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import api, { completeInitialization, setLoggingOut } from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Helper to get stored user data
  const getStoredUserData = useCallback(() => {
    const storedUser = localStorage.getItem('user') || sessionStorage.getItem('user');
    if (storedUser) {
      try {
        return JSON.parse(storedUser);
      } catch (e) {
        return null;
      }
    }
    return null;
  }, []);

  // Helper to clear all auth data
  const clearAuthData = useCallback(() => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('rememberMe');
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('user');
    setUser(null);
  }, []);

  // Helper: decode JWT expiry without library
  const isTokenExpired = useCallback((token) => {
    if (!token) return true;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (!payload.exp) return false; // no exp claim = don't force expire
      return Date.now() >= payload.exp * 1000;
    } catch {
      return true; // malformed token = treat as expired
    }
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      const storedUserData = getStoredUserData();
      
      // Check token expiry BEFORE making any API call
      if (token && isTokenExpired(token)) {
        console.log('JWT expired locally, clearing session');
        clearAuthData();
        setLoading(false);
        completeInitialization();
        return;
      }

      if (token && storedUserData) {
        // First, set the user from stored data immediately for fast UI
        setUser(storedUserData);
        
        try {
          // Then verify token is still valid by calling the API
          const response = await api.get('/auth/me');
          setUser(response.data);
          
          // Update stored user data with fresh data from server
          const isInLocalStorage = localStorage.getItem('authToken') !== null;
          if (isInLocalStorage) {
            localStorage.setItem('user', JSON.stringify(response.data));
          } else {
            sessionStorage.setItem('user', JSON.stringify(response.data));
          }
        } catch (error) {
          // Only clear session on 401 (unauthorized) errors
          // For other errors (network, 500, etc), keep the stored session
          if (error.response?.status === 401) {
            console.log('Token expired or invalid, clearing session');
            clearAuthData();
          } else {
            // Network error or server error - keep the stored user
            console.log('Network/server error during auth verification, keeping stored session');
            // User is already set from storedUserData above
          }
        }
      }
      
      setLoading(false);
      // Mark API initialization as complete
      completeInitialization();
    };
    
    initAuth();
  }, [getStoredUserData, clearAuthData, isTokenExpired]);

  // Periodic token expiry check (every 60s)
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(() => {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      if (token && isTokenExpired(token)) {
        console.log('JWT expired during session, logging out');
        clearAuthData();
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [user, isTokenExpired, clearAuthData]);

  const login = async (email, password, rememberMe = true) => {
    // ALWAYS use localStorage (persistent) — sessionStorage was causing mobile users
    // to be logged out every time they closed the browser/tab. The JWT itself
    // expires in 7 days with sliding refresh, so persistence is safe.
    // The `rememberMe` flag is kept for backward compat with checkbox but no
    // longer changes storage location.
    const response = await api.post('/auth/login', { email, password, remember_me: true });
    const { access_token, user: userData } = response.data;

    localStorage.setItem('authToken', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('rememberMe', 'true');
    // Clear sessionStorage just in case stale data is there
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('user');
    setUser(userData);

    return userData;
  };

  const register = async (name, email, password, leadSource) => {
    const response = await api.post('/auth/register', { name, email, password, lead_source: leadSource || window.location.pathname });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('authToken', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    
    return userData;
  };

  const logout = () => {
    setLoggingOut(true);
    clearAuthData();
    // Reset the flag after a short delay
    setTimeout(() => setLoggingOut(false), 1000);
  };

  // Passwordless session bootstrap (AYEKA V2 free trial): the backend already
  // issued a JWT, so we persist it + user and mark init complete (no password).
  const loginWithToken = (token, userData) => {
    localStorage.setItem('authToken', token);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('rememberMe', 'true');
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('user');
    setUser(userData);
    completeInitialization();
    return userData;
  };

  const value = {
    user,
    loading,
    login,
    register,
    loginWithToken,
    logout
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
